import type { OwnerId } from 'prove-common'
import type { DelegationNode } from './taskDelegationUtils'
import { HIDDEN_DELEGATION_UNITS } from '../config/hiddenDelegationUnits'

/** Configured units and their ancestors remain visible while their subtree has activity. */
export const getEffectiveHiddenDelegationUnits = (
  nodes: DelegationNode[],
  configuredUnits: ReadonlySet<string> = HIDDEN_DELEGATION_UNITS
): ReadonlySet<string> => {
  if (configuredUnits.size === 0) return configuredUnits

  const activeUnits = new Set<string>()
  const visit = (delegations: DelegationNode[]): boolean => {
    let subtreeHasActivity = false
    for (const delegation of delegations) {
      const childHasActivity = visit(delegation.children)
      const hasActivity =
        delegation.plannedQuantity > 0 ||
        (delegation.samplesTaken ?? 0) > 0 ||
        childHasActivity
      if (hasActivity) {
        activeUnits.add(delegation.ownerId.toString())
      }
      subtreeHasActivity ||= hasActivity
    }
    return subtreeHasActivity
  }
  visit(nodes)

  return new Set(
    [...configuredUnits].filter((ownerId) => !activeUnits.has(ownerId))
  )
}

export const isDelegationPathHidden = (
  path: OwnerId[],
  hiddenUnits: ReadonlySet<string> = HIDDEN_DELEGATION_UNITS
): boolean => path.some((ownerId) => hiddenUnits.has(ownerId.toString()))

/** Returns the navigable prefix before the first hidden unit. */
export const truncateBeforeHiddenDelegationUnit = (
  path: OwnerId[],
  hiddenUnits: ReadonlySet<string> = HIDDEN_DELEGATION_UNITS
): OwnerId[] => {
  const hiddenIndex = path.findIndex((ownerId) =>
    hiddenUnits.has(ownerId.toString())
  )
  return hiddenIndex < 0 ? path : path.slice(0, hiddenIndex)
}

export const visiblePlannedQuantity = (
  node: DelegationNode,
  path: OwnerId[],
  hiddenUnits: ReadonlySet<string> = HIDDEN_DELEGATION_UNITS
): number => {
  if (isDelegationPathHidden(path, hiddenUnits)) return 0
  if (node.children.length === 0) return node.plannedQuantity
  return (
    (node.retainedQuantity ?? 0) +
    node.children.reduce(
      (sum, child) =>
        sum +
        visiblePlannedQuantity(child, [...path, child.ownerId], hiddenUnits),
      0
    )
  )
}

export const visibleSamplesTaken = (
  node: DelegationNode,
  path: OwnerId[],
  hiddenUnits: ReadonlySet<string> = HIDDEN_DELEGATION_UNITS
): number => {
  if (isDelegationPathHidden(path, hiddenUnits)) return 0
  return (
    (node.samplesTaken ?? 0) +
    node.children.reduce(
      (sum, child) =>
        sum + visibleSamplesTaken(child, [...path, child.ownerId], hiddenUnits),
      0
    )
  )
}
